import {
    Children,
    cloneElement,
    isValidElement,
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.css';

/**
 * Tooltip — a real, escape-proof tooltip primitive.
 *
 * The collapsed sidebar rail used to fake this with `.sidebar__link[title]:hover::after`,
 * which can never paint: the offset-parent chain runs through `.sidebar__nav`
 * (`overflow-y: auto`) and `.sidebar` (`overflow: hidden`), and no z-index escapes a
 * clip box. This portals to `document.body` and positions itself with `position: fixed`
 * against the trigger's viewport rect, so no ancestor can clip it.
 *
 * Props:
 *   children   — a single element. Gets hover/focus handlers via cloneElement; its own
 *                handlers and its `ref` are left untouched.
 *   label      — tooltip content (string or node). Falls back to the child's `title`,
 *                in which case the `title` attribute is removed (no double tooltip) and
 *                promoted to `aria-label` so an icon-only trigger keeps its name.
 *   placement  — 'right' (default) | 'top' | 'bottom' | 'left'
 *   delay      — ms before a *hover* opens it (default 200). Keyboard focus is instant.
 *   disabled   — render the child untouched.
 *   id         — id for the tooltip node (defaults to a generated one).
 *
 * Behaviour:
 *   - opens on hover AND on keyboard focus (the rail tooltip gives keyboard users
 *     nothing today); a click that focuses the trigger does not open it
 *   - closes on mouseleave, blur, Escape, and pointerdown
 *   - repositions on scroll/resize and clamps itself inside the viewport. Placement is
 *     fixed — it clamps, it does not flip to the opposite side.
 *   - fades out before unmounting; `prefers-reduced-motion` drops the slide, keeps the fade
 */

const PLACEMENTS = new Set(['right', 'top', 'bottom', 'left']);

// Safety net only: the exit is normally driven by `transitionend` on the surface, so
// CSS owns the duration. If that event never lands (element yanked mid-transition,
// transitions disabled at the browser level) we still unmount.
const EXIT_FALLBACK_MS = 500;

/** Viewport-space anchor point the tooltip hangs off. The gap itself lives in CSS. */
function anchorFor(rect, placement) {
    switch (placement) {
        case 'top':
            return { x: rect.left + rect.width / 2, y: rect.top };
        case 'bottom':
            return { x: rect.left + rect.width / 2, y: rect.bottom };
        case 'left':
            return { x: rect.left, y: rect.top + rect.height / 2 };
        case 'right':
        default:
            return { x: rect.right, y: rect.top + rect.height / 2 };
    }
}

/* ── Input-modality tracking ──────────────────────────────────────────────
   Only keyboard focus should open a tooltip: a click that focuses the trigger
   already had its hover. `:focus-visible` is the native answer, but it is not
   observable everywhere (jsdom parses the selector and always answers `false`,
   which would make keyboard support silently untestable), so we track the last
   input modality ourselves and treat `:focus-visible` as a second opinion. */

let keyboardModality = false;
const trackedDocuments = new WeakSet();

function trackModality(doc) {
    if (!doc || trackedDocuments.has(doc)) return;
    trackedDocuments.add(doc);
    const opts = { capture: true, passive: true };
    doc.addEventListener('keydown', () => { keyboardModality = true; }, opts);
    doc.addEventListener('pointerdown', () => { keyboardModality = false; }, opts);
    doc.addEventListener('mousedown', () => { keyboardModality = false; }, opts);
    doc.addEventListener('touchstart', () => { keyboardModality = false; }, opts);
}

function isKeyboardFocus(el) {
    if (keyboardModality) return true;
    if (!el || typeof el.matches !== 'function') return false;
    try {
        return el.matches(':focus-visible');
    } catch {
        return false;
    }
}

export default function Tooltip({
    children,
    label,
    placement = 'right',
    delay = 200,
    disabled = false,
    id,
}) {
    // `open` is the intent, `mounted` is "in the DOM", `visible` drives the transition.
    // Three states (not two) so a re-open during the fade-out still re-runs the entrance.
    const [open, setOpen] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [visible, setVisible] = useState(false);

    const triggerRef = useRef(null);
    const tipRef = useRef(null);
    const surfaceRef = useRef(null);
    const openTimer = useRef(null);
    const exitTimer = useRef(null);

    const generatedId = useId();
    const tooltipId = id || `tooltip-${generatedId}`;
    const side = PLACEMENTS.has(placement) ? placement : 'right';

    const clearTimers = useCallback(() => {
        if (openTimer.current) {
            clearTimeout(openTimer.current);
            openTimer.current = null;
        }
        if (exitTimer.current) {
            clearTimeout(exitTimer.current);
            exitTimer.current = null;
        }
    }, []);

    const place = useCallback(() => {
        const trigger = triggerRef.current;
        const tip = tipRef.current;
        if (!trigger || !tip || typeof trigger.getBoundingClientRect !== 'function') return;

        const rect = trigger.getBoundingClientRect();
        const { x, y } = anchorFor(rect, side);
        tip.style.left = `${x}px`;
        tip.style.top = `${y}px`;

        // Nudge back inside the viewport if the placement pushed it out. No margin
        // constant needed — the CSS gap token already holds it off the trigger.
        const vw = window.innerWidth || document.documentElement?.clientWidth || 0;
        const vh = window.innerHeight || document.documentElement?.clientHeight || 0;
        // A zero-sized viewport (jsdom, offscreen/headless render contexts) is not a
        // collision — clamping against it would slam every tooltip into the corner.
        if (vw <= 0 || vh <= 0) return;

        const box = tip.getBoundingClientRect();
        let dx = 0;
        let dy = 0;
        if (box.right > vw) dx = vw - box.right;
        if (box.left + dx < 0) dx = -box.left;
        if (box.bottom > vh) dy = vh - box.bottom;
        if (box.top + dy < 0) dy = -box.top;
        if (dx || dy) {
            tip.style.left = `${x + dx}px`;
            tip.style.top = `${y + dy}px`;
        }
    }, [side]);

    const show = useCallback((wait) => {
        clearTimers();
        if (wait > 0) {
            openTimer.current = setTimeout(() => {
                openTimer.current = null;
                setOpen(true);
            }, wait);
        } else {
            setOpen(true);
        }
    }, [clearTimers]);

    const hide = useCallback(() => {
        clearTimers();
        setOpen(false);
        exitTimer.current = setTimeout(() => {
            exitTimer.current = null;
            setMounted(false);
        }, EXIT_FALLBACK_MS);
    }, [clearTimers]);

    // Intent → DOM presence. Closing only clears `visible`; the fade-out unmounts later.
    useLayoutEffect(() => {
        if (open) setMounted(true);
        else setVisible(false);
    }, [open]);

    // Position before paint, then flip the visible class on the next frame so the
    // entrance transition actually runs.
    useLayoutEffect(() => {
        if (!mounted || !open) return undefined;
        place();
        const raf = requestAnimationFrame(() => setVisible(true));
        return () => cancelAnimationFrame(raf);
    }, [mounted, open, place]);

    // Follow the trigger while open (capture: true catches nested scrollers).
    useEffect(() => {
        if (!mounted) return undefined;
        const onReflow = () => place();
        window.addEventListener('scroll', onReflow, true);
        window.addEventListener('resize', onReflow);
        return () => {
            window.removeEventListener('scroll', onReflow, true);
            window.removeEventListener('resize', onReflow);
        };
    }, [mounted, place]);

    useEffect(() => {
        if (!mounted) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') hide();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [mounted, hide]);

    useEffect(() => {
        trackModality(typeof document === 'undefined' ? null : document);
    }, []);

    useEffect(() => clearTimers, [clearTimers]);

    if (!isValidElement(children)) return children ?? null;

    const child = Children.only(children);
    const childTitle = typeof child.props.title === 'string' ? child.props.title : undefined;
    const content = label ?? childTitle;
    const usedChildTitle = label == null && childTitle != null;

    if (disabled || content == null || content === '') return child;

    const describedBy = [child.props['aria-describedby'], mounted ? tooltipId : null]
        .filter(Boolean)
        .join(' ');

    // The trigger element comes from the event (`currentTarget`), so we never touch the
    // child's `ref` — no merging, nothing for us to clobber, and no ref handed to
    // cloneElement during render (which react-hooks/refs rejects).
    // Handlers are written inline rather than through a `chain(a, b)` helper for the
    // same reason: that helper would receive a ref-reading callback during render.
    const triggerProps = {
        onMouseEnter: (event) => {
            child.props.onMouseEnter?.(event);
            triggerRef.current = event.currentTarget;
            show(delay);
        },
        onMouseLeave: (event) => {
            child.props.onMouseLeave?.(event);
            hide();
        },
        onFocus: (event) => {
            child.props.onFocus?.(event);
            triggerRef.current = event.currentTarget;
            if (isKeyboardFocus(event.target)) show(0);
        },
        onBlur: (event) => {
            child.props.onBlur?.(event);
            hide();
        },
        onPointerDown: (event) => {
            child.props.onPointerDown?.(event);
            hide();
        },
    };
    if (describedBy) triggerProps['aria-describedby'] = describedBy;
    if (usedChildTitle) {
        // Suppress the native tooltip we are replacing, but keep the accessible name.
        triggerProps.title = undefined;
        if (!child.props['aria-label']) triggerProps['aria-label'] = childTitle;
    }

    return (
        <>
            {/* eslint-disable-next-line react-hooks/refs -- verified false positive. The
                rule flags the `triggerProps` argument, but that object holds closures,
                not ref reads: nothing in it dereferences `triggerRef.current`, and the
                one ref touch (`triggerRef.current = event.currentTarget`) is a WRITE
                that runs later, inside a DOM event handler. cloneElement copies props
                onto a new element and never invokes them, so no ref is read or written
                during render. This is the standard trigger-composition pattern; the
                alternative (a wrapper element) would either change the child's layout
                box or, with `display: contents`, leave no rect to position against. */}
            {cloneElement(child, triggerProps)}
            {mounted && createPortal(
                <div
                    ref={tipRef}
                    id={tooltipId}
                    role="tooltip"
                    className={[
                        'af-tooltip',
                        `af-tooltip--${side}`,
                        visible && 'af-tooltip--visible',
                    ].filter(Boolean).join(' ')}
                >
                    <span
                        ref={surfaceRef}
                        className="af-tooltip__surface"
                        onTransitionEnd={(event) => {
                            if (event.propertyName !== 'opacity') return;
                            if (event.target !== surfaceRef.current) return;
                            if (!open) {
                                clearTimers();
                                setMounted(false);
                            }
                        }}
                    >
                        {content}
                    </span>
                </div>,
                document.body,
            )}
        </>
    );
}

export { Tooltip };
